import React, { useMemo, useRef, useState, useEffect } from "react";
import Dropzone from "./Dropzone";

function prettySize(bytes) {
  if (bytes === 0) return "0 o";
  if (!bytes) return "";
  const units = ["o", "Ko", "Mo", "Go"];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function loadImageFromUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

export default function ImageCompressor() {
  const [file, setFile] = useState(null);
  const [quality, setQuality] = useState(0.8); // 0.4 -> 1
  const [maxWidth, setMaxWidth] = useState(1920);
  const [format, setFormat] = useState("image/webp"); // image/jpeg | image/webp
  const [loading, setLoading] = useState(false);

  const [result, setResult] = useState(null); // { url, blob, name }
  const [beforeSize, setBeforeSize] = useState(null);

  // preview URLs
  const [beforeUrl, setBeforeUrl] = useState(null);
  const beforeUrlRef = useRef(null);

  const cleanupResultUrl = () => {
    if (result?.url) URL.revokeObjectURL(result.url);
  };

  const cleanupBeforeUrl = () => {
    if (beforeUrlRef.current) URL.revokeObjectURL(beforeUrlRef.current);
    beforeUrlRef.current = null;
    setBeforeUrl(null);
  };

  useEffect(() => {
    return () => {
      cleanupResultUrl();
      cleanupBeforeUrl();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savings = useMemo(() => {
    if (!beforeSize || !result?.blob?.size) return null;
    const diff = beforeSize - result.blob.size;
    const pct = (diff / beforeSize) * 100;
    return { diff, pct };
  }, [beforeSize, result]);

  const onPickFile = (f) => {
    cleanupResultUrl();
    cleanupBeforeUrl();

    setResult(null);
    setFile(f || null);
    setBeforeSize(f?.size ?? null);

    if (f) {
      const url = URL.createObjectURL(f);
      beforeUrlRef.current = url;
      setBeforeUrl(url);
    }
  };

  const compress = async () => {
    if (!file || !beforeUrl) return;

    setLoading(true);
    cleanupResultUrl();
    setResult(null);

    try {
      const img = await loadImageFromUrl(beforeUrl);

      // resize (contain)
      const scale = Math.min(1, maxWidth / img.width);
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);

      const blob = await new Promise((resolve, reject) =>
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Compression échouée"))),
          format,
          quality
        )
      );

      const ext = format === "image/webp" ? "webp" : "jpg";
      const base = file.name.replace(/\.\w+$/, "");
      const outName = `${base}-compressed.${ext}`;

      const url = URL.createObjectURL(blob);
      setResult({ url, blob, name: outName });
    } catch (e) {
      console.error(e);
      alert("Erreur pendant la compression.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section style={{ padding: 16 }}>
      <Dropzone
        accept="image/*"
        multiple={false}
        disabled={loading}
        acceptText="Dépose ton image ici"
        helperText="Glisse-dépose une image ou clique sur “Choisir un fichier”"
        onFiles={(files) => onPickFile(files?.[0] || null)}
      />

      {file && (
        <p style={{ textAlign: "center", color: "#666", marginTop: 8 }}>
          Image sélectionnée : <b>{file.name}</b>
        </p>
      )}

      {/* OPTIONS */}
      <div style={{ maxWidth: 460, margin: "12px auto", textAlign: "left" }}>
        <label style={{ display: "block", marginBottom: 8 }}>
          Format de sortie
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            disabled={loading}
            style={{
              width: "100%",
              padding: 10,
              marginTop: 6,
              borderRadius: 5,
              border: "1px solid #ccc",
            }}
          >
            <option value="image/webp">WebP (souvent plus léger)</option>
            <option value="image/jpeg">JPEG</option>
          </select>
        </label>

        <label style={{ display: "block", marginBottom: 8 }}>
          Largeur max (px)
          <input
            type="number"
            min={320}
            max={8000}
            value={maxWidth}
            onChange={(e) => setMaxWidth(Number(e.target.value))}
            disabled={loading}
            style={{
              width: "100%",
              padding: 10,
              marginTop: 6,
              borderRadius: 5,
              border: "1px solid #ccc",
            }}
          />
        </label>

        <label style={{ display: "block", marginBottom: 8 }}>
          Qualité : {Math.round(quality * 100)}%
          <input
            type="range"
            min="0.4"
            max="1"
            step="0.05"
            value={quality}
            onChange={(e) => setQuality(Number(e.target.value))}
            disabled={loading}
            style={{ width: "100%" }}
          />
        </label>
      </div>

      <button onClick={compress} disabled={!file || loading}>
        {loading ? "Compression..." : "Compresser"}
      </button>

      {/* PREVIEW AVANT / APRES */}
      {(beforeUrl || result?.url) && (
        <div
          style={{
            maxWidth: 900,
            margin: "18px auto 0",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 14,
          }}
        >
          {beforeUrl && (
            <div
              style={{
                background: "#fff",
                border: "1px solid #e6e6e6",
                borderRadius: 14,
                padding: 12,
              }}
            >
              <div style={{ fontWeight: 800, color: "#222" }}>Avant</div>
              <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                {beforeSize ? (
                  <>
                    Poids : <b>{prettySize(beforeSize)}</b>
                  </>
                ) : null}
              </div>
              <img
                src={beforeUrl}
                alt="Aperçu avant"
                style={{
                  width: "100%",
                  height: 220,
                  objectFit: "contain",
                  marginTop: 10,
                  borderRadius: 10,
                  background: "#f4f4f4",
                }}
              />
            </div>
          )}

          {result?.url && (
            <div
              style={{
                background: "#fff",
                border: "1px solid #e6e6e6",
                borderRadius: 14,
                padding: 12,
              }}
            >
              <div style={{ fontWeight: 800, color: "#222" }}>Après</div>
              <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                Poids : <b>{prettySize(result.blob.size)}</b>{" "}
                {savings && (
                  <span>
                    (gain : <b>{prettySize(savings.diff)}</b> —{" "}
                    <b>{savings.pct.toFixed(1)}%</b>)
                  </span>
                )}
              </div>
              <img
                src={result.url}
                alt="Aperçu après"
                style={{
                  width: "100%",
                  height: 220,
                  objectFit: "contain",
                  marginTop: 10,
                  borderRadius: 10,
                  background: "#f4f4f4",
                }}
              />

              <div style={{ textAlign: "center", marginTop: 12 }}>
                <a href={result.url} download={result.name}>
                  Télécharger l’image optimisée
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
